import { toPng } from 'html-to-image';
import posthog from 'posthog-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import CashoutModal from './CashoutModal';
import './ParlayTicketSlip.css';

interface ParlayLeg {
  ingest_market_id: string;
  outcome_label: string;
  snap_implied_prob: number | null;
  ends_at: string | null;
  status: string;
  market_question: string;
  market_icon: string | null;
  event_title: string;
  event_image: string | null;
  category: string | null;
  sub_category: string | null;
  sportsTeamData?: {
    team1: { logo?: string };
    team2: { logo?: string };
    selectedTeam: 0 | 1 | 2;
  };
}

interface ParlayTicket {
  parlay_id: string;
  total_amount: number;
  total_odds: number;
  expected_payout: number;
  status: string;
  settlement_date: string;
  created_at: string;
  tx_hash?: string;
  settlement_tx_hash?: string;
  actual_cashout_amount?: number;
  referral_code?: string;
  legs: ParlayLeg[];
}

interface ParlayTicketSlipProps {
  ticket: ParlayTicket;
}

const ParlayTicketSlip: React.FC<ParlayTicketSlipProps> = ({ ticket }) => {
  const slipRef = useRef<HTMLDivElement>(null);
  const [cashoutModalOpen, setCashoutModalOpen] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(ticket.referral_code || null);
  const [isExporting, setIsExporting] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const { user } = useAuth();
  const legsRef = useRef<HTMLDivElement>(null);
  const [legsViewportHeight, setLegsViewportHeight] = useState<number | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Fetch or generate referral code
  useEffect(() => {
    const fetchReferralCode = async () => {
      if (referralCode || !user?.issuer) return;

      try {
        const response = await fetch(`${API_URL}/referral/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_issuer: user.issuer,
            parlay_ticket_id: ticket.parlay_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          setReferralCode(data.code);
        }
      } catch (error) {
        console.error('Failed to generate referral code:', error);
      }
    };

    fetchReferralCode();
  }, [ticket.parlay_id, user?.issuer, referralCode, API_URL]);

  // Convert external image URL to proxied URL
  const getProxiedImageUrl = (originalUrl: string | null): string | null => {
    if (!originalUrl) return null;
    if (originalUrl.startsWith('https://polymarket-upload.s3')) {
      return `${API_URL}/proxy-image?url=${encodeURIComponent(originalUrl)}`;
    }
    return originalUrl;
  };

  // Extract the correct icon for a leg (team logo for sports, market_icon otherwise)
  const getLegIcon = (leg: ParlayLeg): string | null => {
    // If this is a sports bet with team data, use the selected team's logo
    if (leg.sportsTeamData && leg.sportsTeamData.selectedTeam > 0) {
      const selectedTeam = leg.sportsTeamData.selectedTeam === 1
        ? leg.sportsTeamData.team1
        : leg.sportsTeamData.team2;
      if (selectedTeam.logo) {
        return selectedTeam.logo;
      }
    }
    // Fallback to market_icon for non-sports bets or if team logo not available
    return leg.market_icon;
  };

  // Recalculate the combined height of the first two legs to keep a consistent viewport
  const recalcLegsHeight = useCallback(() => {
    const container = legsRef.current;
    if (!container) return;

    // If there are 2 or fewer legs, do not constrain the height
    const legCards = Array.from(container.querySelectorAll<HTMLDivElement>('.leg-card'));
    if (legCards.length <= 2) {
      setLegsViewportHeight(null);
      return;
    }

    // Measure the first two leg elements including margins
    const first = legCards[0];
    const second = legCards[1];
    if (!first || !second) {
      setLegsViewportHeight(null);
      return;
    }

    const firstRect = first.getBoundingClientRect();
    const secondRect = second.getBoundingClientRect();

    // Compute vertical extent from top of first to bottom of second relative to container
    const containerRect = container.getBoundingClientRect();
    const top = Math.min(firstRect.top, secondRect.top) - containerRect.top;
    const bottom = Math.max(firstRect.bottom, secondRect.bottom) - containerRect.top;
    const height = Math.max(0, Math.ceil(bottom - top));

    setLegsViewportHeight(height);
  }, []);

  // Recalculate on mount, when legs change, on window resize, and when export state toggles off
  useEffect(() => {
    if (isExporting) return; // while exporting we expand, avoid constraining
    recalcLegsHeight();
  }, [ticket.legs, recalcLegsHeight, isExporting]);

  useEffect(() => {
    if (isExporting) return;
    const handleResize = () => recalcLegsHeight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [recalcLegsHeight, isExporting]);

  // Observe size changes inside legs (e.g., images loading)
  useEffect(() => {
    if (!legsRef.current || isExporting) return;
    const ro = new ResizeObserver(() => recalcLegsHeight());
    ro.observe(legsRef.current);
    return () => ro.disconnect();
  }, [recalcLegsHeight, isExporting]);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (!showShareTooltip) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.share-button-container')) {
        setShowShareTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareTooltip]);

  const handleCashout = () => {
    setCashoutModalOpen(true);
  };

  const handleShareSlip = () => {
    setShowShareTooltip(!showShareTooltip);
  };

  const handleShareLink = async () => {
    setShowShareTooltip(false);
    try { posthog.capture('parlay:share', { parlay_id: ticket.parlay_id }); } catch {}
    if (!slipRef.current) return;

    try {
      // Ensure referral code exists before screenshot
      if (!referralCode && user?.issuer) {
        const response = await fetch(`${API_URL}/referral/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_issuer: user.issuer,
            parlay_ticket_id: ticket.parlay_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          setReferralCode(data.code);
          // Wait a moment for React to re-render with the code
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Set exporting state for style changes
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 200));

      // Use html-to-image for reliable rendering
      const dataUrl = await toPng(slipRef.current, {
        cacheBust: true,
        pixelRatio: 3, // Higher quality (3x for ultra-sharp images)
        backgroundColor: 'transparent', // Let CSS background image show through
        quality: 1.0, // Maximum quality (0.0 to 1.0)
        width: slipRef.current.offsetWidth * 3, // Explicit width for high-res
        height: slipRef.current.offsetHeight * 3, // Explicit height for high-res
        style: {
          transform: 'scale(3)',
          transformOrigin: 'top left',
          width: slipRef.current.offsetWidth + 'px',
          height: slipRef.current.offsetHeight + 'px'
        },
        filter: (node) => {
          // Exclude tooltip and other UI elements
          if (node.classList?.contains('share-tooltip')) return false;
          if (node.classList?.contains('share-button-container')) return false;
          return true;
        }
      });

      // Reset exporting state
      setIsExporting(false);

      console.log('Image generated successfully');
      console.log('Data URL length:', dataUrl.length);

      // Utilities
      const dataUrlToBlob = async (u: string) => {
        const r = await fetch(u);
        return await r.blob();
      };
      const blobToFile = (b: Blob, filename: string) => new File([b], filename, { type: b.type || 'image/png' });
      const buildLocalReferralUrl = () => referralCode ? `${window.location.origin}/?ref=${referralCode}` : window.location.origin;

      // Build a thumbnail (approximately 1200px width for OG)
      const createThumbnail = async (srcDataUrl: string, targetWidth = 1200): Promise<string> => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve as () => void;
          img.onerror = reject as () => void;
          img.src = srcDataUrl;
        });
        const scale = Math.min(1, targetWidth / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return srcDataUrl;
        ctx.drawImage(img, 0, 0, w, h);
        return canvas.toDataURL('image/png');
      };

      // Create thumbnail and upload to backend to obtain share link with OG tags
      let shareLinkFromBackend: string | null = null;
      try {
        const thumbBase64 = await createThumbnail(dataUrl, 1200);
        const uploadRes = await fetch(`${API_URL}/share/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_base64: thumbBase64, referral_code: referralCode, parlay_ticket_id: ticket.parlay_id })
        });
        if (uploadRes.ok) {
          const payload = await uploadRes.json();
          shareLinkFromBackend = payload.share_url as string;
          try { posthog.capture('parlay:share_link_created', { parlay_id: ticket.parlay_id }); } catch {}
        }
      } catch (e) {
        console.warn('Share upload failed:', e);
      }

      // If we got a share link, prefer sharing the URL
      if (shareLinkFromBackend) {
        try {
          if (typeof navigator.share === 'function') {
            await navigator.share({ title: 'TPC Parlay', url: shareLinkFromBackend });
            try { posthog.capture('parlay:share_link_shared', { parlay_id: ticket.parlay_id }); } catch {}
            return;
          }
        } catch (e) {
          console.warn('Native URL share failed:', e);
        }
        // Clipboard fallback for URL
        try {
          await navigator.clipboard.writeText(shareLinkFromBackend);
          try { posthog.capture('parlay:share_link_clipboard_fallback', { parlay_id: ticket.parlay_id }); } catch {}
          alert('Share link copied to clipboard');
          return;
        } catch (e) {
          console.warn('Clipboard URL copy failed:', e);
        }
      }

      // Fallback: previous image-based sharing flow
      const blob = await dataUrlToBlob(dataUrl);
      const filename = `parlay-slip-${ticket.parlay_id.slice(0, 8)}.png`;
      const file = blobToFile(blob, filename);
      const title = 'TPC Parlay';
      const text = buildLocalReferralUrl();
      try {
        const navAny = navigator as any;
        if (typeof navAny?.canShare === 'function' && navAny.canShare({ files: [file] })) {
          await navAny.share({ files: [file], title, text });
          try { posthog.capture('parlay:share_native_files', { parlay_id: ticket.parlay_id }); } catch {}
          return;
        }
      } catch (e) {
        console.warn('Native file share failed or unsupported:', e);
      }
      // Download as last resort
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = filename;
      link.href = blobUrl;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      try { posthog.capture('parlay:share_download_fallback', { parlay_id: ticket.parlay_id }); } catch {}
      console.log('Download initiated successfully');

    } catch (error) {
      console.error('Error details:', error);
      console.error('Error stack:', error instanceof Error ? error.stack : 'No stack trace available');
      alert('Failed to generate slip image. Please try again.');
      try { posthog.capture('parlay:share_error', { parlay_id: ticket.parlay_id, message: (error as any)?.message || String(error) }); } catch {}
    }
  };

  const handleDownloadImage = async () => {
    setShowShareTooltip(false);
    try { posthog.capture('parlay:share', { parlay_id: ticket.parlay_id }); } catch {}
    if (!slipRef.current) return;

    try {
      // Ensure referral code exists
      if (!referralCode && user?.issuer) {
        const response = await fetch(`${API_URL}/referral/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_issuer: user.issuer,
            parlay_ticket_id: ticket.parlay_id
          })
        });

        if (response.ok) {
          const data = await response.json();
          setReferralCode(data.code);
          // Wait a moment for React to re-render with the code
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Set exporting state for style changes
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 200));

      // Use html-to-image for reliable rendering
      const dataUrl = await toPng(slipRef.current, {
        cacheBust: true,
        pixelRatio: 3, // Higher quality (3x for ultra-sharp images)
        backgroundColor: '#ffffff',
        quality: 1.0, // Maximum quality (0.0 to 1.0)
        width: slipRef.current.offsetWidth * 3, // Explicit width for high-res
        height: slipRef.current.offsetHeight * 3, // Explicit height for high-res
        style: {
          transform: 'scale(3)',
          transformOrigin: 'top left',
          width: slipRef.current.offsetWidth + 'px',
          height: slipRef.current.offsetHeight + 'px'
        },
        filter: (node) => {
          // Exclude tooltip and other UI elements
          if (node.classList?.contains('share-tooltip')) return false;
          if (node.classList?.contains('share-button-container')) return false;
          return true;
        }
      });

      setIsExporting(false);

      // Download the image
      const link = document.createElement('a');
      link.download = `parlay-slip-${ticket.parlay_id.slice(0, 8)}.png`;
      link.href = dataUrl;
      link.click();

      try { posthog.capture('parlay:image_downloaded', { parlay_id: ticket.parlay_id }); } catch {}
      console.log('Download initiated successfully');

    } catch (error) {
      setIsExporting(false);
      console.error('Error generating image:', error);
      alert('Failed to generate image. Please try again.');
      try { posthog.capture('parlay:share_error', { parlay_id: ticket.parlay_id, message: (error as any)?.message || String(error) }); } catch {}
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short', 
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'TBD';
    }
  };

  // Removed per design: per-leg date/time display

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Check if cashout is available for this ticket
  const isCashoutEligible = () => {
    return ticket.status === 'placed' || ticket.status === 'confirmed';
  };

  // Check if ticket is settled
  const isSettled = () => {
    return ticket.status === 'won' || ticket.status === 'paid' || ticket.status === 'lost' || ticket.status === 'cashed_out';
  };

  // Get settlement amount and label based on status
  const getSettlementInfo = () => {
    switch (ticket.status) {
      case 'won':
        return {
          amount: ticket.expected_payout,
          label: 'Won'
        };
      case 'paid':
        return {
          amount: ticket.expected_payout,
          label: 'Won'
        };
      case 'lost':
        return {
          amount: ticket.total_amount,
          label: 'Lost'
        };
      case 'cashed_out':
        return {
          amount: ticket.actual_cashout_amount || ticket.expected_payout,
          label: 'Cashed Out'
        };
      default:
        return {
          amount: ticket.expected_payout,
          label: 'To Win'
        };
    }
  };

  const getSettlementInlineText = () => {
    const { amount, label } = getSettlementInfo();
    const formatted = formatCurrency(amount);
    if (label === 'Won') return `Won ${formatted}`;
    if (label === 'Lost') return `Lost ${formatted}`;
    if (label === 'Cashed Out') return `Cashed Out ${formatted}`;
    return `${label} ${formatted}`;
  };

  const renderSlipContent = () => (
    <>
      {/* Header Section (only for active/eligible cashout) */}
      {isCashoutEligible() && (
        <div className="slip-header">
          <div className="slip-header-content">
            <div className="slip-header-left" />
            <div className="slip-header-right">
              <button className="cashout-button" onClick={handleCashout}>
                Cash out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Legs */}
      <div
        className={`slip-legs ${ticket.legs.length > 2 && !isExporting ? 'scrollable' : ''}`}
        ref={legsRef}
        style={ticket.legs.length > 2 && !isExporting && legsViewportHeight ? { height: `${legsViewportHeight}px` } : undefined}
      >
        {ticket.legs.map((leg, index) => (
          <div key={`${leg.ingest_market_id}-${index}`} className={`leg-card ${
            leg.status === 'won' ? 'won-leg' :
            leg.status === 'lost' ? 'lost-leg' : ''
          }`}>
            <div className="leg-content">
              <div className="leg-icon">
                {getLegIcon(leg) && (
                  <img
                    src={(() => {
                      const legIcon = getLegIcon(leg);
                      if (legIcon && failedImages.has(legIcon)) {
                        return getProxiedImageUrl(leg.event_image) || leg.event_image || legIcon || '';
                      }
                      return getProxiedImageUrl(legIcon) || legIcon || '';
                    })()}
                    alt={leg.event_title}
                    className="market-icon"
                    onLoad={recalcLegsHeight}
                    onError={(e) => {
                      const legIcon = getLegIcon(leg);
                      if (legIcon && !failedImages.has(legIcon)) {
                        const newFailedImages = new Set(failedImages);
                        newFailedImages.add(legIcon);
                        setFailedImages(newFailedImages);
                      }
                    }}
                  />
                )}
              </div>

              <div className="leg-details">
                <div className="leg-title">{leg.event_title}</div>
                <div className="leg-subrow">
                  <span className="leg-subtitle">{leg.market_question}</span>
                  <span className="dot-separator">•</span>
                  <span className={`leg-outcome-text ${leg.outcome_label.toLowerCase()}`}>
                    {leg.outcome_label}
                  </span>
                  {typeof leg.snap_implied_prob === 'number' && !Number.isNaN(leg.snap_implied_prob) && (
                    <>
                      <span className="dot-separator">•</span>
                      <span className="leg-price">
                        {(() => {
                          const p = Math.max(0, Math.min(1, Number(leg.snap_implied_prob)));
                          const cents = Math.round(p * 100);
                          return `${cents}c`;
                        })()}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Centered dashed separator with logo */}
      <div className="slip-separator">
        <img src="/TPC_only.png" alt="TPC" className="separator-logo" />
      </div>

      {/* Settlement amount below separator for resolved slips */}
      {isSettled() && (
        <div className="settlement-below">
          <div
            className={`settlement-inline ${
              ticket.status === 'lost' ||
              (ticket.status === 'cashed_out' && ticket.actual_cashout_amount && ticket.actual_cashout_amount < ticket.total_amount)
                ? 'loss'
                : 'won'
            }`}
          >
            {getSettlementInlineText()}
          </div>
        </div>
      )}

      <div className="slip-footer">
        <div className="footer-left">
          {!isSettled() && (
            <div className="slip-summary">
              <div className="summary-card odds-card">
                <div className="summary-label">Bet</div>
                <div className="summary-value">{formatCurrency(ticket.total_amount)}</div>
              </div>
              <div className="summary-card payout-card">
                <div className="summary-label">{getSettlementInfo().label}</div>
                <div className="summary-value payout-amount">{formatCurrency(getSettlementInfo().amount)}</div>
                <div className="summary-subnote">({ticket.total_odds.toFixed(2)}x)</div>
              </div>
            </div>
          )}
        </div>
        <div className="footer-right" />
      </div>

      {/* Unified pills row: referral on left, Share on right */}
      <div className="footer-pills">
        {referralCode ? (
          <div className="referral-code-display">{referralCode}</div>
        ) : (
          <div className="referral-code-display placeholder" aria-hidden="true">&nbsp;</div>
        )}
        <div className="share-button-container">
          <button className="share-pill" onClick={handleShareSlip} aria-label="Share parlay slip">Share</button>
          {showShareTooltip && (
            <div className="share-tooltip">
              <button onClick={handleShareLink}>Share Link</button>
              <button onClick={handleDownloadImage}>Download Image</button>
            </div>
          )}
        </div>
      </div>

      {/* Cashout Modal */}
      <CashoutModal
        isOpen={cashoutModalOpen}
        onClose={() => setCashoutModalOpen(false)}
        ticket={ticket}
      />
    </>
  );

  return (
    <>
      <div className={`parlay-ticket-slip ${isExporting ? 'exporting' : ''}`} ref={slipRef}>
        {isExporting && (
          <img 
            src="/parlayslip_background.png" 
            alt="" 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1,
              pointerEvents: 'none'
            }}
          />
        )}
        {renderSlipContent()}
      </div>
    </>
  );
};

export default ParlayTicketSlip;